/*! AUTO-GENERATED by scripts/flower-dna/sync-public.mjs — edit engine.mjs + re-run sync */
(function (global) {
/** @typedef {'confirmed'|'provisional'|'blocked'} ProposalStatus */

function loadDnaFromParts(parts) {
  return {
    species: parts.species.species || parts.species,
    composition: parts.composition,
    recipe: parts.recipe,
    colors: parts.colors,
    mapping: parts.mapping,
  }
}

/** Storefront public JSON shape from flower-dna-public.json */
function loadDnaFromPublic(publicJson) {
  return {
    species: publicJson.species,
    composition: publicJson.composition,
    recipe: publicJson.recipe,
    colors: publicJson.colors,
    mapping: publicJson.mapping,
  }
}

function getSelectableAnchors(dna) {
  return dna.species.filter(
    (s) =>
      s.selectableAsCustomerAnchor === true &&
      s.status === 'active' &&
      s.hardBan !== true &&
      s.allowedInRecipes !== false,
  )
}

/**
 * Reliable Shopify bridge: confirmed/active + product handle. Never invent handles.
 * `suggested` / `unknown` are never reliable.
 */
function isReliableMapping(m) {
  if (!m || !m.speciesId) return false
  const status = String(m.status || 'unknown').toLowerCase()
  if (status !== 'confirmed' && status !== 'active') return false
  const handle = m.shopifyProductHandle && String(m.shopifyProductHandle).trim()
  return Boolean(handle)
}

/** All reliable mapping rows for a species (color variants allowed). */
function getReliableMappings(dna, speciesId) {
  return mappingsFor(dna, speciesId).filter(isReliableMapping)
}

function getReliableMapping(dna, speciesId) {
  const list = getReliableMappings(dna, speciesId)
  return list[0] || null
}

/**
 * Build speciesId → availability from Shopify collection products + DNA mapping.
 * collectionProducts: [{ handle, productId, variantId, available }]
 * A species is available if ANY confirmed color/product mapping is in the collection and available.
 */
function buildAvailabilityFromCollection(dna, collectionProducts) {
  const byHandle = new Map()
  for (const p of collectionProducts || []) {
    if (!p || !p.handle) continue
    byHandle.set(String(p.handle).toLowerCase(), p)
  }

  /** @type {Record<string, { available: boolean, mapped: boolean, reason: string|null, variantId: string|null, productHandle: string|null }>} */
  const index = {}

  for (const s of getSelectableAnchors(dna)) {
    index[s.id] = {
      available: false,
      mapped: false,
      reason: 'unmapped',
      variantId: null,
      productHandle: null,
    }
  }

  const bySpecies = new Map()
  for (const m of dna.mapping?.mappings || []) {
    if (!m?.speciesId) continue
    if (!bySpecies.has(m.speciesId)) bySpecies.set(m.speciesId, [])
    bySpecies.get(m.speciesId).push(m)
  }

  for (const [speciesId, rows] of bySpecies) {
    const reliable = rows.filter(isReliableMapping)
    if (!reliable.length) {
      index[speciesId] = {
        available: false,
        mapped: false,
        reason: 'unmapped',
        variantId: null,
        productHandle: null,
      }
      continue
    }

    let best = null
    for (const m of reliable) {
      const handle = String(m.shopifyProductHandle).trim()
      const product = byHandle.get(handle.toLowerCase())
      if (!product) {
        if (!best) {
          best = {
            available: false,
            mapped: true,
            reason: 'not_in_availability_collection',
            variantId: m.shopifyVariantId ? String(m.shopifyVariantId) : null,
            productHandle: handle,
          }
        }
        continue
      }
      const available = product.available === true
      if (available) {
        best = {
          available: true,
          mapped: true,
          reason: null,
          variantId: m.shopifyVariantId
            ? String(m.shopifyVariantId)
            : product.variantId
              ? String(product.variantId)
              : null,
          productHandle: handle,
        }
        break
      }
      best = {
        available: false,
        mapped: true,
        reason: 'unavailable_today',
        variantId: m.shopifyVariantId
          ? String(m.shopifyVariantId)
          : product.variantId
            ? String(product.variantId)
            : null,
        productHandle: handle,
      }
    }
    index[speciesId] = best
  }

  return index
}

/** DNA anchors that are mapped AND available in today’s collection. */
function getVisibleAnchors(dna, availabilityIndex) {
  return getSelectableAnchors(dna).filter((s) => {
    const row = availabilityIndex?.[s.id]
    return !!(row && row.mapped && row.available === true)
  })
}

function isSpeciesLiveAvailable(speciesId, availabilityIndex) {
  const row = availabilityIndex?.[speciesId]
  return !!(row && row.mapped && row.available === true)
}

/**
 * Confirmed mappings present in the Theme Editor availability collection and available for sale.
 * Includes image/title from collection JSON when provided.
 */
function getLiveMappedProducts(dna, collectionProducts) {
  const byHandle = new Map()
  for (const p of collectionProducts || []) {
    if (!p || !p.handle) continue
    byHandle.set(String(p.handle).toLowerCase(), p)
  }

  const out = []
  for (const m of dna.mapping?.mappings || []) {
    if (!isReliableMapping(m)) continue
    if (m.speciesId === 'panicum') continue
    const species = (dna.species || []).find((s) => s.id === m.speciesId)
    if (isBanned(species, dna)) continue

    const handle = String(m.shopifyProductHandle).trim()
    const product = byHandle.get(handle.toLowerCase())
    if (!product || product.available !== true) continue

    out.push({
      speciesId: m.speciesId,
      colorId: m.colorId || null,
      shopifyProductHandle: handle,
      shopifyProductId: m.shopifyProductId ? String(m.shopifyProductId) : product.productId ? String(product.productId) : null,
      shopifyVariantId: m.shopifyVariantId
        ? String(m.shopifyVariantId)
        : product.variantId
          ? String(product.variantId)
          : null,
      matchedShopifyTitle: m.matchedShopifyTitle || product.title || null,
      observedShopifyTitle: m.observedShopifyTitle || null,
      displayTitle: product.title || m.matchedShopifyTitle || handle,
      imageUrl: product.imageUrl || m.imageUrl || null,
      priceCents:
        typeof product.priceCents === 'number'
          ? product.priceCents
          : typeof product.price === 'number'
            ? product.price
            : null,
      status: 'confirmed',
      catalogAvailable: true,
      todayCollectionMember: true,
    })
  }
  return out
}

function getLiveProductsForSpecies(dna, speciesId, collectionProducts) {
  return getLiveMappedProducts(dna, collectionProducts).filter((p) => p.speciesId === speciesId)
}

/** Species that may appear as a customer hoofdbloem choice (color or flower path). */
function isHoofdboemChoiceSpecies(species, dna) {
  if (!species || isBanned(species, dna)) return false
  if (isFillerSpecies(species)) return false
  const roles = species.roles || []
  if (
    roles.includes('green_base') ||
    roles.includes('green_structure') ||
    roles.includes('green_forbidden')
  ) {
    return false
  }
  if (species.selectableAsCustomerAnchor) return true
  return (
    roles.includes('heart') ||
    roles.includes('supporting') ||
    roles.includes('seasonal_typical') ||
    roles.includes('anchor_candidate')
  )
}

/**
 * Live mapped products in a specific color that can be chosen as hoofdbloem.
 * Availability + confirmed mapping only — never invents products.
 */
function getLiveProductsForColor(dna, colorId, collectionProducts) {
  if (!colorId) return []
  return getLiveMappedProducts(dna, collectionProducts)
    .filter((p) => p.colorId === colorId)
    .filter((p) => {
      const sp = (dna.species || []).find((s) => s.id === p.speciesId)
      return isHoofdboemChoiceSpecies(sp, dna)
    })
    .sort((a, b) => String(a.displayTitle).localeCompare(String(b.displayTitle)))
}

/** Catalog colors that currently have ≥1 live available hoofdbloem product. */
function getLiveColors(dna, collectionProducts) {
  const live = getLiveMappedProducts(dna, collectionProducts)
  const colorIds = new Set()
  for (const row of live) {
    if (!row.colorId) continue
    const sp = (dna.species || []).find((s) => s.id === row.speciesId)
    if (!isHoofdboemChoiceSpecies(sp, dna)) continue
    colorIds.add(row.colorId)
  }
  const catalog = dna.colors?.colors || []
  return [...colorIds]
    .map((id) => {
      const c = catalog.find((x) => x.id === id)
      const products = getLiveProductsForColor(dna, id, collectionProducts)
      return {
        id,
        label: c?.labels?.nl || c?.labels?.en || id,
        labels: c?.labels || { nl: id },
        status: 'live',
        selectable: true,
        productCount: products.length,
      }
    })
    .filter((c) => c.productCount > 0)
    .sort((a, b) => a.id.localeCompare(b.id))
}

/** Customer anchors that have a live mapped product in this color today. */
function getLiveAnchorsForColor(dna, colorId, collectionProducts) {
  const live = getLiveProductsForColor(dna, colorId, collectionProducts)
  const ids = new Set(
    live
      .map((p) => p.speciesId)
      .filter((id) => {
        const sp = (dna.species || []).find((s) => s.id === id)
        return sp && sp.selectableAsCustomerAnchor && !isBanned(sp, dna)
      }),
  )
  return getSelectableAnchors(dna).filter((s) => ids.has(s.id))
}

/** Representative image for an anchor card (first live product). */
function getAnchorCardMedia(dna, speciesId, collectionProducts) {
  const products = getLiveProductsForSpecies(dna, speciesId, collectionProducts)
  if (!products.length) return null
  return {
    imageUrl: products[0].imageUrl || null,
    title: products[0].displayTitle,
    colorIds: products.map((p) => p.colorId).filter(Boolean),
  }
}

function getSelectableColors(dna) {
  const colors = dna.colors?.colors
  if (!Array.isArray(colors) || colors.length === 0) return []
  return colors.filter((c) => c.selectable === true && c.status === 'confirmed')
}

function isBanned(species, dna) {
  if (!species) return true
  if (species.hardBan === true) return true
  if (species.allowedInRecipes === false) return true
  if (species.status === 'excluded') return true
  const never = dna.composition?.greenRules?.never || []
  if (never.includes(species.id)) return true
  return false
}

function byId(dna) {
  const map = new Map()
  for (const s of dna.species) map.set(s.id, s)
  return map
}

function isFlowerSupportCandidate(s, anchorId, dna) {
  if (!s || s.id === anchorId) return false
  if (isBanned(s, dna)) return false
  if (s.status !== 'active') return false
  const roles = s.roles || []
  if (roles.includes('green_base') || roles.includes('green_structure') || roles.includes('green_forbidden')) {
    return false
  }
  return (
    roles.includes('supporting') ||
    roles.includes('filler') ||
    roles.includes('filler_bloom') ||
    roles.includes('filler_air') ||
    roles.includes('seasonal_typical') ||
    roles.includes('heart')
  )
}

function isFillerSpecies(s) {
  if (!s) return false
  const roles = s.roles || []
  return (
    roles.includes('filler') ||
    roles.includes('filler_air') ||
    roles.includes('filler_bloom')
  )
}

function supportPool(dna, anchorId, availabilityIndex, requireLiveAvailability) {
  return dna.species
    .filter((s) => isFlowerSupportCandidate(s, anchorId, dna))
    .filter((s) => {
      if (!requireLiveAvailability) return true
      return isSpeciesLiveAvailable(s.id, availabilityIndex)
    })
    .sort((a, b) => a.id.localeCompare(b.id))
}

function fillerPool(dna, anchorId, availabilityIndex, requireLiveAvailability) {
  return supportPool(dna, anchorId, availabilityIndex, requireLiveAvailability)
    .filter(isFillerSpecies)
    .filter((s) => {
      // Never use unmapped fillers (Hypericum etc. wait for confirmed mapping)
      return !!getReliableMapping(dna, s.id)
    })
    .sort((a, b) => {
      const rank = (id) => (id === 'gypsophila' ? 0 : id === 'hypericum' ? 1 : 2)
      const d = rank(a.id) - rank(b.id)
      return d !== 0 ? d : a.id.localeCompare(b.id)
    })
}

function nonFillerSupportPool(dna, anchorId, availabilityIndex, requireLiveAvailability) {
  return supportPool(dna, anchorId, availabilityIndex, requireLiveAvailability).filter(
    (s) => !isFillerSpecies(s),
  )
}

function minTotalStems(dna) {
  const n = dna.recipe?.compositionConstraints?.minTotalStems
  return typeof n === 'number' && n > 0 ? n : 8
}

function totalStemCount(lines) {
  return (lines || []).reduce((sum, l) => {
    return sum + (typeof l.quantity === 'number' ? l.quantity : 0)
  }, 0)
}

function proposalHasFiller(proposal, dna) {
  const index = byId(dna)
  return (proposal.lines || []).some((l) => isFillerSpecies(index.get(l.speciesId)))
}

function compositionTargets(dna) {
  const c = dna.recipe?.compositionConstraints || {}
  return {
    minTotal: typeof c.minTotalStems === 'number' ? c.minTotalStems : 8,
    targetTotal: typeof c.targetTotalStems === 'number' ? c.targetTotalStems : 10,
    maxTotal: typeof c.maxTotalStems === 'number' ? c.maxTotalStems : 12,
    anchorShareMin: typeof c.anchorShareMin === 'number' ? c.anchorShareMin : 0.4,
    anchorShareMax: typeof c.anchorShareMax === 'number' ? c.anchorShareMax : 0.6,
    supportMin: typeof c.standardSupportingSpeciesMin === 'number' ? c.standardSupportingSpeciesMin : 1,
    supportMax: typeof c.standardSupportingSpeciesMax === 'number' ? c.standardSupportingSpeciesMax : 2,
    requireFiller: c.requireFiller === true,
    fillerPreferred: c.fillerPreferred !== false,
    fillerQtyMin: typeof c.fillerPreferredStemsMin === 'number' ? c.fillerPreferredStemsMin : 2,
    fillerQtyMax: typeof c.fillerPreferredStemsMax === 'number' ? c.fillerPreferredStemsMax : 3,
    neverPadWithFiller: c.neverPadWithFiller !== false,
  }
}

function maxQtyForSpecies(species, ruleSet) {
  if (!species) return null
  if (ruleSet === 'STANDARD' && typeof species.standard?.maxStems === 'number') {
    return species.standard.maxStems
  }
  if (ruleSet === 'FOCUS' && typeof species.focus?.maxStems === 'number') {
    return species.focus.maxStems
  }
  if (isFillerSpecies(species)) {
    if (typeof species.standard?.maxStems === 'number') return species.standard.maxStems
    return 3
  }
  // Soft structural cap for non-filler supports with UNKNOWN max (not fillers — those stay ≤3)
  return ruleSet === 'FOCUS' ? 4 : 8
}

function intentionalFillerQty(species, dna) {
  const t = compositionTargets(dna)
  const hardMax = maxQtyForSpecies(species, 'STANDARD')
  const preferred = species.standard?.preferredStems
  let min = t.fillerQtyMin
  let max = t.fillerQtyMax
  if (preferred && typeof preferred.min === 'number') min = preferred.min
  if (preferred && typeof preferred.max === 'number') max = preferred.max
  if (typeof hardMax === 'number') {
    max = Math.min(max, hardMax)
    min = Math.min(min, max)
  }
  // Prefer top of preferred range when max allows (typically 3)
  return max
}

/**
 * Raise quantities until min total stems.
 * NEVER pad with filler when neverPadWithFiller (STANDARD commercial rule).
 * Prefer bumping non-filler supports, then soft-capped supports; anchor only within hard max.
 */
function ensureMinTotalStems(lines, dna, ruleSet, minTotal, opts = {}) {
  const index = byId(dna)
  const neverPadWithFiller = opts.neverPadWithFiller !== false
  const next = lines.map((l) => ({ ...l }))
  let total = totalStemCount(next)
  if (total >= minTotal) return { ok: true, lines: next }

  const rows = next.map((l, i) => ({ i, l, sp: index.get(l.speciesId) }))
  const bumpOrder = [
    ...rows.filter((x) => x.sp && !isFillerSpecies(x.sp) && x.l.speciesId !== opts.anchorId),
    ...rows.filter((x) => x.sp && x.l.speciesId === opts.anchorId),
    ...(neverPadWithFiller
      ? []
      : rows.filter((x) => isFillerSpecies(x.sp))),
  ]

  let guard = 0
  while (total < minTotal && guard < 200) {
    guard += 1
    let progressed = false
    for (const item of bumpOrder) {
      if (total >= minTotal) break
      const sp = item.sp
      const row = next[item.i]
      if (row.quantity == null) continue
      const max = maxQtyForSpecies(sp, ruleSet)
      if (typeof max === 'number' && row.quantity >= max) continue
      row.quantity += 1
      total += 1
      progressed = true
    }
    if (!progressed) break
  }

  if (total < minTotal) {
    return { ok: false, lines: next, reason: 'cannot_reach_min_total_stems' }
  }
  return { ok: true, lines: next }
}

function mappingsFor(dna, speciesId) {
  const list = dna.mapping?.mappings || []
  return list.filter((m) => m.speciesId === speciesId)
}

function mappingFor(dna, speciesId) {
  return mappingsFor(dna, speciesId)[0] || null
}

/**
 * @param {object} dna
 * @param {string} speciesId
 * @param {Map|object|Record} availability
 */
function resolvePurchasable(dna, speciesId, availability = {}) {
  const map =
    availability instanceof Map
      ? availability
      : new Map(Object.entries(availability || {}))
  const m = getReliableMapping(dna, speciesId)
  if (!m) {
    return { purchasable: false, reason: 'unmapped_shopify_species', variantId: null }
  }
  const avail = map.get(speciesId)
  if (avail && typeof avail === 'object') {
    if (avail.mapped === false) {
      return { purchasable: false, reason: 'unmapped_shopify_species', variantId: null }
    }
    if (avail.available === false) {
      return {
        purchasable: false,
        reason: avail.reason || 'unavailable_today',
        variantId: avail.variantId || m.shopifyVariantId || null,
      }
    }
    if (avail.available === true && avail.mapped === true) {
      return {
        purchasable: true,
        reason: null,
        variantId: avail.variantId || m.shopifyVariantId || null,
      }
    }
  }
  if (!map.has(speciesId)) {
    return {
      purchasable: false,
      reason: 'availability_not_resolved',
      variantId: m.shopifyVariantId || null,
    }
  }
  return {
    purchasable: false,
    reason: 'unavailable_today',
    variantId: m.shopifyVariantId || null,
  }
}

function line(speciesId, quantity, dna, availability, opts = {}) {
  const purch = resolvePurchasable(dna, speciesId, availability)
  return {
    speciesId,
    quantity: quantity === null || quantity === undefined ? null : quantity,
    quantityStatus: opts.quantityStatus || (quantity == null ? 'UNKNOWN' : 'from_dna'),
    shopifyVariantId: purch.variantId,
    purchasable: purch.purchasable,
    purchasableReason: purch.reason,
  }
}

function signature(proposal) {
  const parts = (proposal.lines || [])
    .map((l) => `${l.speciesId}:${l.quantity ?? 'x'}`)
    .sort()
  return parts.join('|')
}

function speciesSetKey(proposal) {
  return (proposal.lines || [])
    .map((l) => l.speciesId)
    .sort()
    .join('+')
}

function validateNoBanned(proposal, dna) {
  const index = byId(dna)
  for (const l of proposal.lines || []) {
    if (l.speciesId === 'pistachio') return false
    if (isBanned(index.get(l.speciesId), dna)) return false
  }
  return true
}

function applyChrysanthemumCap(lines, dna, ruleSet) {
  if (ruleSet !== 'STANDARD') return { ok: true, lines }
  const members = new Set(dna.composition?.chrysanthemumGroupRule?.memberSpeciesIds || [])
  const max =
    dna.composition?.chrysanthemumGroupRule?.standard?.maxStemsInLargeRecipe ?? null
  if (max == null) return { ok: true, lines }
  let total = 0
  for (const l of lines) {
    if (members.has(l.speciesId) && typeof l.quantity === 'number') total += l.quantity
  }
  if (total > max) return { ok: false, reason: 'chrysanthemum_group_max_exceeded' }
  return { ok: true, lines }
}

function standardAnchorQty(anchor, dna) {
  const t = compositionTargets(dna)
  const max = anchor.standard?.maxStems
  if (typeof max === 'number' && max > 0) {
    // Dominant within hard species limit (may be below 40–60% share — hard max wins)
    return { qty: max, status: 'from_dna', note: `STANDARD maxStems=${max}` }
  }
  // Soft guidance only when max UNKNOWN — ~50% of target, within share band
  const soft = Math.round(t.targetTotal * ((t.anchorShareMin + t.anchorShareMax) / 2))
  const qty = Math.max(2, Math.min(soft, Math.floor(t.targetTotal * t.anchorShareMax)))
  return {
    qty,
    status: 'soft_share_guidance',
    note: 'standard.maxStems UNKNOWN — soft 40–60% share of targetTotal',
  }
}

function focusAnchorQty(anchor) {
  const g = anchor.focus?.dominantStemGuidance
  if (g && typeof g.approxStems === 'number' && g.approxStems > 0) {
    return {
      qty: g.approxStems,
      status: g.hardness === 'example_direction_only' ? 'dna_example_direction' : 'from_dna',
      requiresRuleCompletion: false,
      note: g.note || null,
    }
  }
  return {
    qty: null,
    status: 'UNKNOWN',
    requiresRuleCompletion: true,
    note: 'focus.dominantStemGuidance UNKNOWN — geen verzonnen aantal',
  }
}

function buildFocusProposal(dna, anchor, nonFillerPool, fillers, availability, pickIndex) {
  const maxSupport =
    anchor.focus?.maxSupportingFlowerSpecies ??
    dna.recipe?.focusVase?.lisianthusExampleDirection?.maxSupportingFlowerSpecies ??
    dna.recipe?.compositionConstraints?.focusMaxSupportingIncludingFiller ??
    2

  const supports = []
  if (fillers.length) {
    const filler = fillers[pickIndex % fillers.length]
    supports.push(filler)
    if (maxSupport >= 2 && nonFillerPool.length) {
      const other = nonFillerPool[pickIndex % nonFillerPool.length]
      if (other && other.id !== filler.id) supports.push(other)
    }
  } else if (nonFillerPool.length) {
    // Filler preferred, not required — Focus may use 1–2 non-filler supports
    for (let i = 0; i < Math.min(maxSupport, nonFillerPool.length); i++) {
      supports.push(nonFillerPool[(pickIndex + i) % nonFillerPool.length])
    }
  }

  const aq = focusAnchorQty(anchor)
  const lines = [line(anchor.id, aq.qty, dna, availability, { quantityStatus: aq.status })]
  for (const s of supports) {
    const startQty = isFillerSpecies(s) ? intentionalFillerQty(s, dna) : 1
    lines.push(
      line(s.id, startQty, dna, availability, {
        quantityStatus: isFillerSpecies(s)
          ? 'filler_preferred_range'
          : 'structural_placeholder_not_dna_limit',
        roleHint: isFillerSpecies(s) ? 'filler' : 'support',
      }),
    )
  }

  const minTotal = minTotalStems(dna)
  const ensured = ensureMinTotalStems(lines, dna, 'FOCUS', minTotal, {
    neverPadWithFiller: true,
    anchorId: anchor.id,
  })
  const finalLines = ensured.lines

  const compatUnknown =
    dna.composition?.greenRules?.compatibilityMatrixStatus === 'UNKNOWN' ||
    dna.composition?.greenRules?.compatibilityMatrix == null

  /** @type {ProposalStatus} */
  let status = 'provisional'
  const reasons = []
  if (!ensured.ok) reasons.push(ensured.reason || 'cannot_reach_min_total_stems')
  if (aq.requiresRuleCompletion) reasons.push('requiresRuleCompletion: Focus-qty UNKNOWN voor deze anchor')
  if (compatUnknown) reasons.push('compatibiliteit UNKNOWN — niet Flower DNA approved')
  if (!fillers.length) reasons.push('no_filler_available_focus_without_filler')

  const chrys = applyChrysanthemumCap(finalLines, dna, 'FOCUS')
  if (!chrys.ok) {
    return {
      slot: 0,
      ruleSet: 'FOCUS',
      role: 'FOCUS_VASE',
      status: 'blocked',
      reasons: [chrys.reason],
      lines: finalLines,
      requiresRuleCompletion: true,
      dnaApproved: false,
      purchasable: false,
      title: 'Focus Vase',
      explanation:
        'De gekozen bloem centraal zetten en de vaas bewust rustig houden — geblokkeerd door limiet.',
      anchorId: anchor.id,
    }
  }

  const supportSpeciesCount = finalLines.filter((l) => l.speciesId !== anchor.id).length
  if (supportSpeciesCount > maxSupport) {
    return {
      slot: 0,
      ruleSet: 'FOCUS',
      role: 'FOCUS_VASE',
      status: 'blocked',
      reasons: ['focus_too_many_supporting_species'],
      lines: finalLines,
      requiresRuleCompletion: true,
      dnaApproved: false,
      purchasable: false,
      title: 'Focus Vase',
      anchorId: anchor.id,
    }
  }

  const filler = supports.find(isFillerSpecies)

  return {
    slot: 0,
    ruleSet: 'FOCUS',
    role: 'FOCUS_VASE',
    status,
    reasons,
    lines: finalLines,
    requiresRuleCompletion: !!aq.requiresRuleCompletion || !ensured.ok,
    dnaApproved: false,
    purchasable: false,
    title: 'Focus Vase',
    explanation:
      'De gekozen bloem centraal zetten en de vaas bewust rustig houden (max. 1–2 steunsoorten).',
    anchorId: anchor.id,
    fillerSpeciesId: filler?.id || null,
  }
}

function combinations(pool, size, limit) {
  const out = []
  const n = pool.length
  if (size === 0) return [[]]
  if (size > n) return out

  function rec(start, acc) {
    if (out.length >= limit) return
    if (acc.length === size) {
      out.push(acc.slice())
      return
    }
    for (let i = start; i < n; i++) {
      acc.push(pool[i])
      rec(i + 1, acc)
      acc.pop()
      if (out.length >= limit) return
    }
  }
  rec(0, [])
  return out
}

function allocateStandardSupportQuantities(dna, anchor, supports, anchorQty) {
  const t = compositionTargets(dna)
  const fillers = supports.filter(isFillerSpecies)
  const nonFillers = supports.filter((s) => !isFillerSpecies(s))
  const qtyById = new Map()

  let fillerBudget = 0
  for (const f of fillers) {
    const q = intentionalFillerQty(f, dna)
    qtyById.set(f.id, q)
    fillerBudget += q
  }

  let remaining = t.targetTotal - anchorQty - fillerBudget
  if (nonFillers.length === 0) {
    return qtyById
  }

  // At least 1 stem per non-filler support
  for (const s of nonFillers) {
    qtyById.set(s.id, 1)
  }
  remaining -= nonFillers.length

  let guard = 0
  let idx = 0
  while (remaining > 0 && guard < 100) {
    guard += 1
    const s = nonFillers[idx % nonFillers.length]
    idx += 1
    const max = maxQtyForSpecies(s, 'STANDARD')
    const cur = qtyById.get(s.id) || 0
    if (typeof max === 'number' && cur >= max) {
      if (nonFillers.every((x) => (qtyById.get(x.id) || 0) >= (maxQtyForSpecies(x, 'STANDARD') || 0))) {
        break
      }
      continue
    }
    qtyById.set(s.id, cur + 1)
    remaining -= 1
  }

  return qtyById
}

function buildStandardProposal(dna, anchor, supports, availability, slotIndex) {
  const t = compositionTargets(dna)
  const aq = standardAnchorQty(anchor, dna)
  if (aq.qty == null) {
    return {
      slot: slotIndex,
      ruleSet: 'STANDARD',
      role: 'STANDARD_OR_OTHER',
      status: 'blocked',
      reasons: ['anchor_standard_qty_unknown'],
      lines: [line(anchor.id, null, dna, availability)],
      requiresRuleCompletion: true,
      dnaApproved: false,
      purchasable: false,
      title: `Voorstel ${slotIndex + 1}`,
      anchorId: anchor.id,
    }
  }

  const supportQtys = allocateStandardSupportQuantities(dna, anchor, supports, aq.qty)
  const lines = [
    line(anchor.id, aq.qty, dna, availability, { quantityStatus: aq.status }),
  ]

  for (const s of supports) {
    const qty = supportQtys.get(s.id) || 1
    lines.push(
      line(s.id, qty, dna, availability, {
        quantityStatus: isFillerSpecies(s) ? 'filler_preferred_range' : 'support_allocation',
        roleHint: isFillerSpecies(s) ? 'filler' : 'support',
      }),
    )
  }

  if (typeof anchor.standard?.maxStems === 'number') {
    const al = lines.find((l) => l.speciesId === anchor.id)
    if (al && al.quantity != null && al.quantity > anchor.standard.maxStems) {
      return { status: 'blocked', reasons: ['anchor_exceeds_standard_max'], lines }
    }
  }

  // Cap total at maxTotal by trimming non-filler supports first (never increase filler for pad)
  let total = totalStemCount(lines)
  if (total > t.maxTotal) {
    const index = byId(dna)
    let guard = 0
    while (total > t.maxTotal && guard < 100) {
      guard += 1
      let trimmed = false
      for (let i = lines.length - 1; i >= 0; i--) {
        const sp = index.get(lines[i].speciesId)
        if (!sp || lines[i].speciesId === anchor.id || isFillerSpecies(sp)) continue
        if (lines[i].quantity > 1) {
          lines[i].quantity -= 1
          total -= 1
          trimmed = true
          break
        }
      }
      if (!trimmed) break
    }
  }

  const ensured = ensureMinTotalStems(lines, dna, 'STANDARD', t.minTotal, {
    neverPadWithFiller: true,
    anchorId: anchor.id,
  })
  const finalLines = ensured.lines

  // Enforce filler max after any allocation
  const index = byId(dna)
  for (const l of finalLines) {
    const sp = index.get(l.speciesId)
    if (!isFillerSpecies(sp) || typeof l.quantity !== 'number') continue
    const max = maxQtyForSpecies(sp, 'STANDARD')
    if (typeof max === 'number' && l.quantity > max) {
      return {
        slot: slotIndex,
        ruleSet: 'STANDARD',
        role: 'STANDARD_OR_OTHER',
        status: 'blocked',
        reasons: ['filler_exceeds_standard_max'],
        lines: finalLines,
        requiresRuleCompletion: false,
        dnaApproved: false,
        purchasable: false,
        title: `Voorstel ${slotIndex + 1}`,
        anchorId: anchor.id,
      }
    }
  }

  if (typeof anchor.standard?.maxStems === 'number') {
    const al = finalLines.find((l) => l.speciesId === anchor.id)
    if (al && al.quantity != null && al.quantity > anchor.standard.maxStems) {
      return { status: 'blocked', reasons: ['anchor_exceeds_standard_max'], lines: finalLines }
    }
  }

  const chrys = applyChrysanthemumCap(finalLines, dna, 'STANDARD')
  if (!chrys.ok) {
    return {
      slot: slotIndex,
      ruleSet: 'STANDARD',
      role: 'STANDARD_OR_OTHER',
      status: 'blocked',
      reasons: [chrys.reason],
      lines: finalLines,
      requiresRuleCompletion: false,
      dnaApproved: false,
      purchasable: false,
      title: `Voorstel ${slotIndex + 1}`,
      explanation: 'Combinatie overschrijdt chrysant-groepslimiet.',
      anchorId: anchor.id,
    }
  }

  if (!ensured.ok) {
    return {
      slot: slotIndex,
      ruleSet: 'STANDARD',
      role: 'STANDARD_OR_OTHER',
      status: 'blocked',
      reasons: [ensured.reason || 'cannot_reach_min_total_stems'],
      lines: finalLines,
      requiresRuleCompletion: true,
      dnaApproved: false,
      purchasable: false,
      title: `Voorstel ${slotIndex + 1}`,
      anchorId: anchor.id,
    }
  }

  const compatUnknown =
    dna.composition?.greenRules?.compatibilityMatrixStatus === 'UNKNOWN' ||
    dna.composition?.greenRules?.compatibilityMatrix == null

  const reasons = []
  if (aq.status === 'soft_share_guidance') reasons.push('anchor STANDARD qty soft guidance (max UNKNOWN)')
  if (compatUnknown) reasons.push('compatibiliteit UNKNOWN — niet Flower DNA approved')
  if (!finalLines.every((l) => l.purchasable)) reasons.push('Shopify mapping/availability incompleet')

  const filler = supports.find(isFillerSpecies)
  const finalTotal = totalStemCount(finalLines)

  return {
    slot: slotIndex,
    ruleSet: 'STANDARD',
    role: 'STANDARD_OR_OTHER',
    status: 'provisional',
    reasons,
    lines: finalLines,
    requiresRuleCompletion: false,
    dnaApproved: false,
    purchasable: false,
    title: `Voorstel ${slotIndex + 1}`,
    explanation: filler
      ? 'Andere samenstelling met jouw hoofdbloem en vulbloem.'
      : 'Andere samenstelling met jouw hoofdbloem.',
    anchorId: anchor.id,
    fillerSpeciesId: filler?.id || null,
    stemTotal: finalTotal,
  }
}

/**
 * Deterministic diversity score: prefer unused supports, prefer filler when preferred,
 * penalize near-duplicate sets. Higher is better.
 */
function scoreStandardCandidate(supports, usedSupportCounts, usedSets, t) {
  let score = 0
  const ids = supports.map((s) => s.id).sort()
  const setKey = ids.join('+')
  if (usedSets.has(setKey)) return -1000

  for (const s of supports) {
    const used = usedSupportCounts.get(s.id) || 0
    score += 10 - used * 3
    if (isFillerSpecies(s) && t.fillerPreferred) score += 5
    if (!isFillerSpecies(s) && used === 0) score += 8
  }
  // Prefer 1–2 supporting species (already constrained)
  score += supports.length
  // Stable tie-break by sorted ids
  score += 1 / (1 + ids.join(',').length)
  return score
}

function buildStandardSupportCombos(nonFillers, fillers, t) {
  const combos = []
  const maxSupport = t.supportMax // total aanvullende soorten (1–2), filler telt mee

  // With filler (preferred path): filler alleen, of filler + 1 non-filler
  for (const filler of fillers) {
    combos.push([filler])
    if (maxSupport >= 2) {
      for (let ni = 0; ni < nonFillers.length; ni++) {
        combos.push([filler, nonFillers[ni]])
      }
    }
  }

  // Without filler (allowed): 1 of 2 non-fillers
  if (!t.requireFiller) {
    for (let n = Math.max(1, t.supportMin); n <= Math.min(maxSupport, nonFillers.length); n++) {
      for (const pair of combinations(nonFillers, n, 40)) {
        combos.push([...pair])
      }
    }
  }

  return combos
}

/**
 * Generate exactly 6 proposals for flower mode.
 * @param {{ dna: object, anchorId: string, availability?: object, collectionProducts?: array, requireLiveAvailability?: boolean }} input
 */
function generateFlowerProposals(input) {
  const {
    dna,
    anchorId,
    availability: availabilityInput,
    collectionProducts,
    requireLiveAvailability = false,
    forceAsAnchor = false,
  } = input
  const index = byId(dna)
  const anchor = index.get(anchorId)

  if (!anchor || isBanned(anchor, dna)) {
    return {
      ok: false,
      error: 'invalid_anchor',
      proposals: [],
    }
  }
  if (!anchor.selectableAsCustomerAnchor && !forceAsAnchor) {
    return {
      ok: false,
      error: 'invalid_anchor',
      proposals: [],
    }
  }

  const availabilityIndex =
    availabilityInput && Object.keys(availabilityInput).length
      ? availabilityInput instanceof Map
        ? Object.fromEntries(availabilityInput)
        : availabilityInput
      : collectionProducts
        ? buildAvailabilityFromCollection(dna, collectionProducts)
        : {}

  if (requireLiveAvailability) {
    if (!isSpeciesLiveAvailable(anchorId, availabilityIndex)) {
      return {
        ok: false,
        error: 'anchor_not_available_today',
        proposals: [],
      }
    }
  }

  const fillers = fillerPool(dna, anchorId, availabilityIndex, requireLiveAvailability)
  const nonFillers = nonFillerSupportPool(dna, anchorId, availabilityIndex, requireLiveAvailability)
  const availability = availabilityIndex
  const t = compositionTargets(dna)

  const proposals = []

  // Slot 0 — FOCUS (filler preferred, max 2 supporting)
  proposals.push(buildFocusProposal(dna, anchor, nonFillers, fillers, availability, 0))

  const usedSets = new Set(
    proposals[0].status !== 'blocked' ? [speciesSetKey(proposals[0])] : [],
  )
  const usedSig = new Set(proposals[0].status !== 'blocked' ? [signature(proposals[0])] : [])
  const usedFillers = new Set(
    proposals[0].fillerSpeciesId ? [proposals[0].fillerSpeciesId] : [],
  )
  const usedSupportCounts = new Map()
  for (const l of proposals[0].lines || []) {
    if (l.speciesId === anchorId) continue
    usedSupportCounts.set(l.speciesId, (usedSupportCounts.get(l.speciesId) || 0) + 1)
  }

  const standardCombos = buildStandardSupportCombos(nonFillers, fillers, t)

  // Greedy deterministic diversity: re-score after each pick
  const localUsedSets = new Set(usedSets)
  const localSupportCounts = new Map(usedSupportCounts)
  const localUsedSig = new Set(usedSig)

  while (proposals.length < 6) {
    let best = null
    let bestScore = -Infinity
    let bestIndex = Infinity

    for (let i = 0; i < standardCombos.length; i++) {
      const supports = standardCombos[i]
      const provisionalScore = scoreStandardCandidate(
        supports,
        localSupportCounts,
        localUsedSets,
        t,
      )
      if (provisionalScore < -500) continue
      if (provisionalScore < bestScore) continue
      if (provisionalScore === bestScore && i >= bestIndex) continue

      const p = buildStandardProposal(dna, anchor, supports, availability, proposals.length)
      if (p.status === 'blocked') continue
      if (t.requireFiller && !proposalHasFiller(p, dna)) continue
      if (totalStemCount(p.lines) < t.minTotal) continue
      const setKey = speciesSetKey(p)
      const sg = signature(p)
      if (localUsedSets.has(setKey) || localUsedSig.has(sg)) continue
      if (!p.lines.some((l) => l.speciesId === anchorId)) continue
      if (!validateNoBanned(p, dna)) continue
      if (requireLiveAvailability) {
        const allLive = p.lines.every((l) =>
          isSpeciesLiveAvailable(l.speciesId, availabilityIndex),
        )
        if (!allLive) continue
      }

      best = p
      bestScore = provisionalScore
      bestIndex = i
    }

    if (!best) break

    best.slot = proposals.length
    localUsedSets.add(speciesSetKey(best))
    localUsedSig.add(signature(best))
    for (const l of best.lines) {
      if (l.speciesId === anchorId) continue
      localSupportCounts.set(l.speciesId, (localSupportCounts.get(l.speciesId) || 0) + 1)
    }
    if (best.fillerSpeciesId) usedFillers.add(best.fillerSpeciesId)
    proposals.push(best)
  }

  while (proposals.length < 6) {
    proposals.push({
      slot: proposals.length,
      ruleSet: 'STANDARD',
      role: 'STANDARD_OR_OTHER',
      status: 'blocked',
      reasons: ['onvoldoende verschillende steuncombinaties binnen DNA/availability'],
      lines: [line(anchorId, standardAnchorQty(anchor, dna).qty, dna, availability)],
      requiresRuleCompletion: true,
      dnaApproved: false,
      purchasable: false,
      title: `Voorstel ${proposals.length + 1}`,
      explanation: 'Geen unieke combinatie meer mogelijk zonder regels te verzinnen.',
      anchorId,
    })
  }

  return {
    ok: true,
    mode: 'flower',
    anchorId,
    proposals: proposals.slice(0, 6),
    fillersUsed: [...usedFillers],
    fillerRequired: t.requireFiller,
    fillerPreferred: t.fillerPreferred,
  }
}

/**
 * Color mode — uses same flower proposal engine after a live color→anchor pick.
 * Without anchorId: returns candidate anchors (no invented proposals).
 * With anchorId: requires a live mapped product for that color+species.
 */
function generateColorProposals(input) {
  const {
    dna,
    colorId,
    anchorId = null,
    availability = {},
    collectionProducts = [],
    requireLiveAvailability = true,
  } = input

  const liveColors = getLiveColors(dna, collectionProducts)
  if (!liveColors.length) {
    return {
      ok: false,
      error: 'color_matrix_incomplete',
      message:
        'Geen beschikbare kleuren vandaag: er zijn geen confirmed gemapte ankers in de gekozen availability-collectie.',
      proposals: [],
      candidates: [],
    }
  }

  const color = liveColors.find((c) => c.id === colorId)
  if (!color) {
    return { ok: false, error: 'invalid_color', proposals: [], candidates: [] }
  }

  const candidates = getLiveAnchorsForColor(dna, colorId, collectionProducts)
  if (!candidates.length) {
    return {
      ok: false,
      error: 'no_anchors_for_color',
      message: 'Geen beschikbare hoofdbloemen in deze kleur vandaag.',
      proposals: [],
      candidates: [],
    }
  }

  if (!anchorId) {
    return {
      ok: false,
      error: 'anchor_required',
      message: 'Kies een hoofdbloem in deze kleur.',
      proposals: [],
      candidates,
      colorId,
    }
  }

  if (!candidates.some((c) => c.id === anchorId)) {
    return {
      ok: false,
      error: 'anchor_not_available_for_color',
      message: 'Deze hoofdbloem is vandaag niet beschikbaar in de gekozen kleur.',
      proposals: [],
      candidates,
    }
  }

  const liveProducts = getLiveProductsForSpecies(dna, anchorId, collectionProducts).filter(
    (p) => p.colorId === colorId,
  )
  if (!liveProducts.length) {
    return {
      ok: false,
      error: 'anchor_not_available_today',
      proposals: [],
      candidates,
    }
  }

  const batch = generateFlowerProposals({
    dna,
    anchorId,
    availability,
    collectionProducts,
    requireLiveAvailability,
  })
  if (!batch.ok) return batch
  return {
    ...batch,
    mode: 'color',
    colorId,
    selectedProduct: liveProducts[0],
  }
}

function normSearch(s) {
  return String(s || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9àáäâèéëêìíïîòóöôùúüûñç\s-]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function colorLabelNl(dna, colorId) {
  if (!colorId) return ''
  const c = (dna.colors?.colors || []).find((x) => x.id === colorId)
  return c?.labels?.nl || c?.labels?.en || colorId
}

/**
 * Search live mapped products in the availability collection.
 * Matches title, species labels/aliases, color labels. Banned never returned.
 * @param {{ colorId?: string|null }} [opts] — when set, only products in that color
 */
function searchLiveProducts(dna, collectionProducts, query, opts = {}) {
  const q = normSearch(query)
  if (!q || q.length < 2) return []
  const tokens = q.split(' ').filter(Boolean)
  const colorFilter = opts.colorId || null
  const live = getLiveMappedProducts(dna, collectionProducts)
  const out = []
  for (const p of live) {
    if (colorFilter && p.colorId !== colorFilter) continue
    const sp = (dna.species || []).find((s) => s.id === p.speciesId)
    if (!sp || isBanned(sp, dna)) continue
    if (!isHoofdboemChoiceSpecies(sp, dna) && !sp.selectableAsCustomerAnchor) {
      // Allow search hits only for choosable hoofdbloem species (same as color cards)
      continue
    }
    const hay = normSearch(
      [
        p.displayTitle,
        p.matchedShopifyTitle,
        p.observedShopifyTitle,
        sp.labels?.nl,
        sp.labels?.en,
        sp.id,
        ...(sp.aliases || []),
        p.colorId,
        colorLabelNl(dna, p.colorId),
      ]
        .filter(Boolean)
        .join(' '),
    )
    if (!tokens.every((t) => hay.includes(t))) continue
    out.push({
      ...p,
      speciesLabel: sp.labels?.nl || sp.labels?.en || sp.id,
      colorLabel: colorLabelNl(dna, p.colorId),
      canBeCustomerAnchor: !!sp.selectableAsCustomerAnchor,
      forceAsAnchorEligible: !sp.selectableAsCustomerAnchor,
    })
  }
  return out.sort((a, b) => String(a.displayTitle).localeCompare(String(b.displayTitle)))
}

function pickLiveProductForSpecies(dna, speciesId, collectionProducts, preferredColorId, usedOrientalColors) {
  const products = getLiveProductsForSpecies(dna, speciesId, collectionProducts)
  if (!products.length) return null
  if (speciesId === 'lily_oriental') {
    const avoid = usedOrientalColors || new Set()
    const ok = products.filter((p) => !p.colorId || !avoid.has(p.colorId))
    const pool = ok.length ? ok : products
    if (preferredColorId) {
      const hit = pool.find((p) => p.colorId === preferredColorId)
      if (hit) return hit
    }
    return pool[0]
  }
  if (preferredColorId) {
    const hit = products.find((p) => p.colorId === preferredColorId)
    if (hit) return hit
  }
  return products[0]
}

/**
 * Attach storefront display fields (photo, color, price) to proposal lines.
 * Does not invent DNA quantities. Oriental wit/roze not mixed in one proposal.
 */
function enrichProposalForDisplay(dna, proposal, collectionProducts, selectedAnchorProduct) {
  const usedOriental = new Set()
  if (selectedAnchorProduct?.speciesId === 'lily_oriental' && selectedAnchorProduct.colorId) {
    usedOriental.add(selectedAnchorProduct.colorId)
  }

  const displayLines = []
  for (const l of proposal.lines || []) {
    let product = null
    if (
      selectedAnchorProduct &&
      selectedAnchorProduct.speciesId === l.speciesId &&
      (proposal.anchorId === l.speciesId || l.speciesId === selectedAnchorProduct.speciesId)
    ) {
      // Prefer the concrete chosen anchor product for the anchor line
      if (l.speciesId === (proposal.anchorId || selectedAnchorProduct.speciesId)) {
        product = selectedAnchorProduct
      }
    }
    if (!product) {
      const preferred =
        l.speciesId === selectedAnchorProduct?.speciesId
          ? selectedAnchorProduct?.colorId
          : null
      product = pickLiveProductForSpecies(
        dna,
        l.speciesId,
        collectionProducts,
        preferred,
        usedOriental,
      )
    }
    if (product?.speciesId === 'lily_oriental' && product.colorId) {
      usedOriental.add(product.colorId)
    }

    const sp = (dna.species || []).find((s) => s.id === l.speciesId)
    const unit = product && typeof product.priceCents === 'number' ? product.priceCents : null
    const qty = typeof l.quantity === 'number' ? l.quantity : null
    displayLines.push({
      speciesId: l.speciesId,
      quantity: qty,
      quantityStatus: l.quantityStatus,
      needsQuantity: qty == null,
      shopifyVariantId: product?.shopifyVariantId || l.shopifyVariantId || null,
      purchasable: !!(product && product.shopifyVariantId && qty != null && qty > 0),
      displayName: sp?.labels?.nl || sp?.labels?.en || l.speciesId,
      colorId: product?.colorId || null,
      colorLabel: colorLabelNl(dna, product?.colorId),
      productTitle: product?.displayTitle || product?.matchedShopifyTitle || null,
      imageUrl: product?.imageUrl || null,
      unitPriceCents: unit,
      lineTotalCents: unit != null && qty != null ? unit * qty : null,
      isAnchor: l.speciesId === proposal.anchorId,
    })
  }

  const allPriced = displayLines.every((d) => d.lineTotalCents != null)
  const totalCents = allPriced
    ? displayLines.reduce((sum, d) => sum + d.lineTotalCents, 0)
    : null
  const needsQuantity = displayLines.some((d) => d.needsQuantity)
  const cartReady =
    !needsQuantity &&
    displayLines.every((d) => d.shopifyVariantId && d.quantity > 0) &&
    displayLines.length > 0

  const isFocus = proposal.ruleSet === 'FOCUS'
  return {
    slot: proposal.slot,
    ruleSet: proposal.ruleSet,
    anchorId: proposal.anchorId,
    customerTitle: isFocus ? 'Focus Vase' : `Voorstel ${(proposal.slot ?? 0) + 1}`,
    customerSubtitle: isFocus
      ? 'Jouw gekozen bloem staat centraal.'
      : 'Andere samenstelling met jouw hoofdbloem.',
    focusHint: isFocus
      ? 'Rustig samengesteld met maximaal twee aanvullende bloemsoorten.'
      : null,
    displayLines,
    totalCents,
    needsQuantity,
    cartReady,
  }
}

function buildCartItemsFromDisplay(displayProposal) {
  if (!displayProposal?.cartReady) {
    return { ok: false, error: 'not_cart_ready', items: [] }
  }
  const items = displayProposal.displayLines.map((l) => ({
    id: Number(l.shopifyVariantId),
    quantity: l.quantity,
  }))
  if (items.some((i) => !i.id || !i.quantity)) {
    return { ok: false, error: 'invalid_variant', items: [] }
  }
  return { ok: true, items, error: null }
}

/** Validation helpers for tests */
function assertBatchInvariants(result, anchorId, dna) {
  const errors = []
  if (!result.ok) return ['batch_not_ok']
  if (result.proposals.length !== 6) errors.push('not_exactly_6')
  const focus = result.proposals.filter((p) => p.ruleSet === 'FOCUS')
  if (focus.length !== 1) errors.push('not_exactly_1_focus')
  const minTotal = minTotalStems(dna)
  for (const p of result.proposals) {
    if (!p.lines?.some((l) => l.speciesId === anchorId)) errors.push(`anchor_missing_${p.slot}`)
    if (!validateNoBanned(p, dna)) errors.push(`banned_in_${p.slot}`)
    if (p.lines?.some((l) => l.speciesId === 'pistachio')) errors.push('pistachio_present')
    if (p.lines?.some((l) => l.speciesId === 'panicum')) errors.push('panicum_present')
    if (p.status !== 'blocked') {
      const requireFiller = dna.recipe?.compositionConstraints?.requireFiller === true
      if (requireFiller && !proposalHasFiller(p, dna)) errors.push(`missing_filler_${p.slot}`)
      // Gyps / fillers must never exceed STANDARD max (typically 3)
      for (const l of p.lines || []) {
        const sp = (dna.species || []).find((s) => s.id === l.speciesId)
        if (!sp || !isFillerSpecies(sp) || typeof l.quantity !== 'number') continue
        if (p.ruleSet === 'STANDARD' && typeof sp.standard?.maxStems === 'number') {
          if (l.quantity > sp.standard.maxStems) {
            errors.push(`filler_over_max_${p.slot}_${l.speciesId}`)
          }
        }
      }
      const knownQty = (p.lines || []).every(
        (l) => l.quantity == null || typeof l.quantity === 'number',
      )
      if (knownQty && !p.requiresRuleCompletion) {
        const total = totalStemCount(p.lines)
        // Only enforce when all quantities known (no nulls)
        if ((p.lines || []).every((l) => typeof l.quantity === 'number') && total < minTotal) {
          errors.push(`below_min_stems_${p.slot}`)
        }
      }
      if (p.ruleSet === 'FOCUS') {
        const supportCount = (p.lines || []).filter((l) => l.speciesId !== anchorId).length
        if (supportCount > 2) errors.push(`focus_too_many_supports_${p.slot}`)
      }
    }
  }
  // near-duplicates among non-blocked standards
  const keys = result.proposals
    .filter((p) => p.ruleSet === 'STANDARD' && p.status !== 'blocked')
    .map(speciesSetKey)
  if (new Set(keys).size !== keys.length) errors.push('near_duplicate_standards')
  return errors
}

function assertLisianthusLimits(result, dna) {
  const errors = []
  const focus = result.proposals.find((p) => p.ruleSet === 'FOCUS')
  const standards = result.proposals.filter((p) => p.ruleSet === 'STANDARD')
  const fLine = focus?.lines?.find((l) => l.speciesId === 'lisianthus')
  if (fLine && fLine.quantity != null && fLine.quantity <= 3) {
    // Focus must NOT be capped by standard max 3 when DNA has ~10 guidance
    const anchor = dna.species.find((s) => s.id === 'lisianthus')
    if (anchor?.focus?.dominantStemGuidance?.approxStems === 10 && fLine.quantity < 10) {
      errors.push('focus_blocked_by_standard_max')
    }
  }
  if (fLine && fLine.quantity === 10) {
    /* ok direction */
  }
  for (const p of standards) {
    const l = p.lines?.find((x) => x.speciesId === 'lisianthus')
    if (l && typeof l.quantity === 'number' && l.quantity > 3) {
      errors.push('standard_lisianthus_over_max')
    }
  }
  return errors
}

  global.FhRecipeEngine = {
    loadDnaFromParts: loadDnaFromParts,
    loadDnaFromPublic: loadDnaFromPublic,
    getSelectableAnchors: getSelectableAnchors,
    getVisibleAnchors: getVisibleAnchors,
    getSelectableColors: getSelectableColors,
    getLiveMappedProducts: getLiveMappedProducts,
    getLiveProductsForSpecies: getLiveProductsForSpecies,
    getLiveColors: getLiveColors,
    getLiveAnchorsForColor: getLiveAnchorsForColor,
    getLiveProductsForColor: getLiveProductsForColor,
    isHoofdboemChoiceSpecies: isHoofdboemChoiceSpecies,
    getAnchorCardMedia: getAnchorCardMedia,
    searchLiveProducts: searchLiveProducts,
    enrichProposalForDisplay: enrichProposalForDisplay,
    buildCartItemsFromDisplay: buildCartItemsFromDisplay,
    isFillerSpecies: isFillerSpecies,
    totalStemCount: totalStemCount,
    proposalHasFiller: proposalHasFiller,
    isBanned: isBanned,
    isReliableMapping: isReliableMapping,
    getReliableMapping: getReliableMapping,
    getReliableMappings: getReliableMappings,
    buildAvailabilityFromCollection: buildAvailabilityFromCollection,
    isSpeciesLiveAvailable: isSpeciesLiveAvailable,
    resolvePurchasable: resolvePurchasable,
    generateFlowerProposals: generateFlowerProposals,
    generateColorProposals: generateColorProposals,
    assertBatchInvariants: assertBatchInvariants,
    assertLisianthusLimits: assertLisianthusLimits
  };
})(typeof window !== 'undefined' ? window : globalThis);
